/**
 * RISKS-SITES.ROUTE
 * Make the tour of all site
 */
import { useEffect, useState } from "react"
import { useAppDispatch, useAppSelector } from "@/core/root-reducer"
import siteStore from "@/entities/site/model/site.store"
import SitesNav from "@/entities/site/ui/SitesNav"
import { faCheck, faPen, faPencilAlt, faPlus, faRoute, faTrashAlt, faUndo } from "@fortawesome/free-solid-svg-icons"
import mapStore from "@/entities/map/model/map.store"
import PagePlaceholder from "@/components/page-placeholder"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"
import Card from "@/components/card"
import MapContainer, { MapEditMode } from "@/entities/map/ui/MapContainer"
import SiteOverview from "@/entities/site/ui/SiteOverview"
import MapEditModal from "@/entities/map/ui/modal/MapEditModal"
import PageHeader from "@/components/page-header"
import physicalRiskStore from "@/entities/physical-risk/model/physical-risk.store"
import PhysicalRiskEditModal from "@/entities/physical-risk/ui/modal/PhysicalRiskEditModal"
import SiteEditModal from "@/entities/site/ui/modal/SiteEditModal"
import SiteDeleteModal from "@/entities/site/ui/modal/SiteDeleteModal"
import PageLoader from "@/components/page-loader"
import { STATUS_LOADING } from "@/redux/_status.types"
import NavEstablishment from "@/nav/nav.establishment"
import { useRisksSpace } from "@/core/risks-space.context"

export default function RisksSitesRoute() {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { getPath, isReadonly } = useRisksSpace()
  const { siteId } = useParams()
  const [searchParams] = useSearchParams()

  //Selectors
  const isActive = useAppSelector(siteStore.selectors.isActive)
  const sites = useAppSelector(siteStore.selectors.list)
  const sitesForMap = useAppSelector(siteStore.selectors.listForMap)
  const maps = useAppSelector(mapStore.selectors.list)
  const otherSites = useAppSelector(siteStore.selectors.withoutMap)
  const showOtherSites = otherSites.length > 0 && (searchParams.get("view") === "other" || !maps.length)
  const activeMap = useAppSelector(mapStore.selectors.active)
  const activeSite = useAppSelector(siteStore.selectors.active)
  const activeRisk = useAppSelector(physicalRiskStore.selectors.active)

  //State
  const [modal, setModal] = useState<
    null | "MODAL_MAP_EDIT" | "MODAL_RISK_ADD" | "MODAL_SITE_EDIT" | "MODAL_SITE_DELETE"
  >(null)
  const [mapEditAction, setMapEditAction] = useState<
    "add-site" | "add-line" | "update-site" | "cancel-edit" | "validate-edit" | "delete-point" | null
  >(null)
  const [mapEditMode, setMapEditMode] = useState<MapEditMode>(null)
  const isMapEditing = mapEditMode !== null
  const [canValidateMapEdit, setCanValidateMapEdit] = useState(false)
  const [canDeleteMapPoint, setCanDeleteMapPoint] = useState(false)
  const routeSite = siteId ? sites.find((item) => item.id === siteId) : null

  useEffect(() => {
    const site = sites.find((item) => item.id === siteId)

    if (!siteId) {
      if (activeSite.EstablishmentId) dispatch(siteStore.actions.setActive(null))
      if (activeRisk.EstablishmentId) dispatch(physicalRiskStore.actions.setActive(null))
      if (isReadonly && maps.length && !maps.some((map) => map.id === activeMap.id)) {
        dispatch(mapStore.actions.setActive(maps[0]))
      }
      return
    }

    if (!site) {
      if (activeSite.EstablishmentId) dispatch(siteStore.actions.setActive(null))
      return
    }

    if (activeSite.id !== site.id) {
      dispatch(siteStore.actions.setActive(site))
      dispatch(physicalRiskStore.actions.setActive(null))
    }

    const map = maps.find((item) => item.id === site.MapId)
    if (map && activeMap.id !== map.id) dispatch(mapStore.actions.setActive(map))
  }, [
    activeMap.id,
    activeRisk.EstablishmentId,
    activeSite.EstablishmentId,
    activeSite.id,
    dispatch,
    isReadonly,
    maps,
    siteId,
    sites
  ])

  //Content of the page
  function PageContent() {
    if (!siteId && showOtherSites) {
      return (
        <div className="flex-1 min-w-0 overflow-y-auto p-[30px]">
          <PageHeader section="Risques" title="Autres lieux de travail" />
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-[20px]">
            {otherSites.map((site) => (
              <Card key={site.id} className="_hover" onClick={() => navigate(getPath(`/sites/${site.id}?view=other`))}>
                <b>{site.name}</b>
              </Card>
            ))}
          </div>
        </div>
      )
    }
    if (!maps.length && !siteId)
      return (
        <PagePlaceholder
          image="report"
          button={isReadonly ? undefined : "Ajouter un niveau"}
          title={
            isReadonly ? "Les lieux de travail n’ont pas été définis" : "Vous n'avez pas défini de lieu de travail"
          }
          onClick={isReadonly ? undefined : () => setModal("MODAL_MAP_EDIT")}
        />
      )
    else {
      return (
        <div className="flex-1 flex h-full min-w-0 relative flex-col">
          {!siteId && (
            <div className="px-40 pt-20">
              <PageHeader
                section="Risques"
                title="Lieux de travail"
                subtitle={isReadonly ? undefined : "Mettez à jour les lieux de travail pour votre établissement."}
              />
            </div>
          )}
          {siteId ? (
            <div className="flex-1 min-w-0 min-h-0 h-full">
              {isActive && activeSite.id === siteId ? (
                <SiteOverview />
              ) : routeSite ? (
                <PageLoader status={STATUS_LOADING} forceLoading className="m-auto" />
              ) : (
                <PagePlaceholder
                  title="Lieu de travail introuvable"
                  button="Retour aux lieux de travail"
                  onClick={() => navigate(getPath("/sites"))}
                />
              )}
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 px-40 pb-[30px] ">
              <MapContainer
                sites={sitesForMap}
                editAction={mapEditAction}
                onEditActionHandled={() => setMapEditAction(null)}
                onEditModeChange={setMapEditMode}
                onEditCanValidateChange={setCanValidateMapEdit}
                onEditCanDeletePointChange={setCanDeleteMapPoint}
                onSelectSite={(site) => navigate(getPath(`/sites/${site.id}`))}
              />
            </div>
          )}
        </div>
      )
    }
  }

  const content = (
    <>
      {modal === "MODAL_MAP_EDIT" && <MapEditModal onClose={() => setModal(null)} />}
      {modal === "MODAL_SITE_EDIT" && <SiteEditModal onClose={() => setModal(null)} />}
      {modal === "MODAL_SITE_DELETE" && <SiteDeleteModal onClose={() => setModal(null)} />}
      {modal === "MODAL_RISK_ADD" && <PhysicalRiskEditModal onClose={() => setModal(null)} />}
      <div className="flex fixed top-[48px] left-0 h-[calc(100%-48px)] app-content-width">
        {(!isReadonly || maps.length > 0 || otherSites.length > 0) && <SitesNav />}
        {PageContent()}
      </div>
    </>
  )

  return (
    <NavEstablishment
      actions={[
        ...(siteId && isActive
          ? [
              {
                id: "EDIT_SITE",
                icon: faPencilAlt,
                label: "Modifier lieu de travail",
                onClick: () => setModal("MODAL_SITE_EDIT")
              },
              {
                id: "DELETE_SITE",
                icon: faTrashAlt,
                label: "Supprimer lieu de travail",
                onClick: () => setModal("MODAL_SITE_DELETE")
              }
            ]
          : maps.length
            ? [
                {
                  id: "ADD_MAP",
                  icon: faPlus,
                  label: "Ajouter un niveau",
                  onClick: () => {
                    dispatch(mapStore.actions.setActive(null))
                    setModal("MODAL_MAP_EDIT")
                  }
                },
                ...(!showOtherSites && sitesForMap.length
                  ? [
                      {
                        id: "UPDATE_SITE",
                        icon: faPen,
                        label: "Modifier lieu",
                        onClick: () => setMapEditAction("update-site")
                      }
                    ]
                  : [])
              ]
            : []),
        ...(isMapEditing && !siteId
          ? [
              {
                id: "CANCEL_MAP_EDIT",
                icon: faUndo,
                label: "Annuler",
                isLarge: true,
                onClick: () => setMapEditAction("cancel-edit")
              }
            ]
          : []),
        ...(canDeleteMapPoint && !siteId
          ? [
              {
                id: "DELETE_MAP_POINT",
                icon: faTrashAlt,
                label: "Supprimer le point",
                className: "warning" as const,
                isLarge: true,
                onClick: () => setMapEditAction("delete-point")
              }
            ]
          : []),
        ...(canValidateMapEdit && !siteId
          ? [
              {
                id: "VALIDATE_MAP_EDIT",
                icon: faCheck,
                label: mapEditMode === "line" ? "Valider la circulation" : "Valider la mise à jour",
                className: "secondary" as const,
                isLarge: true,
                onClick: () => setMapEditAction("validate-edit")
              }
            ]
          : []),
        ...(mapEditMode === "site" && !canValidateMapEdit && !siteId
          ? [
              {
                id: "ADD_TRAFFIC_ROUTE",
                icon: faRoute,
                label: "Ajouter circulation",
                isLarge: true,
                onClick: () => setMapEditAction("add-line")
              }
            ]
          : []),
        ...(!isMapEditing && (siteId || (!showOtherSites && maps.length > 0))
          ? [
              {
                id: "ADD_SITE",
                icon: faPlus,
                label: siteId ? "Ajouter risque" : "Ajouter lieu",
                className: "primary" as const,
                isLarge: true,
                onClick: () => (siteId ? setModal("MODAL_RISK_ADD") : setMapEditAction("add-site"))
              }
            ]
          : [])
      ]}
      onTabClick={() => dispatch(siteStore.actions.setActive(null))}>
      {content}
    </NavEstablishment>
  )
}
